import type { GameContext } from '../core/context';
import type { System } from '../core/system';
import { defaultSave, hasProgress, type GameSettings, type SaveData } from './saveData';
import type { SavedArsenal } from '../weapons/arsenal';
import { recordRun, type JobRecord, type MedalTier } from '../missions/medals';
import { SaveStore, browserStorage, type LoadStatus, type StorageLike } from './saveStore';

/**
 * Save service — the single owner of persistence. Other modules read settings/unlocks here and call
 * the mutators; the system debounces writes and flushes on mission completion / tab hide.
 */
export interface SaveService {
  /** Live save object. Treat as read-only; use the mutators so the change is persisted. */
  readonly data: SaveData;
  readonly status: LoadStatus;
  readonly settings: GameSettings;
  /** True when there is real progress to continue from. */
  readonly hasProgress: boolean;
  updateSettings(patch: Partial<GameSettings>): void;
  hasUnlock(id: string): boolean;
  addUnlock(id: string): void;
  isMissionComplete(id: string): boolean;
  /** Record what the player carries (weapons module; debounced like money). Ignored while suspended. */
  setArsenal(a: SavedArsenal): void;
  /**
   * A story job was finished (first time or a replay): fold its stamp and time into `jobRecords` and write.
   * Returns what changed (null while suspended — online runs are never recorded).
   */
  recordJobRun(id: string, medal: MedalTier, seconds: number): { record: JobRecord; medalUp: boolean; timeUp: boolean } | null;
  /** Write now. Returns false if storage is unavailable. */
  flush(): boolean;
  /** Wipe progress (settings are kept) and apply the fresh state to the running game. */
  newGame(): void;
  markDirty(): void;
  /**
   * Online play (called only by src/net): while suspended, money/missions/stats from the online session
   * are NOT recorded into the single-player save (economy data never goes to browser storage online).
   * Settings still save.
   */
  setSuspended(suspended: boolean): void;
  readonly suspended: boolean;
}

declare module '../core/services' {
  interface ServiceMap {
    save: SaveService;
  }
}

declare module '../core/events' {
  interface GameEvents {
    /** Settings changed (menu, load, reset). Render/camera/audio react to this. */
    settingsChanged: { settings: GameSettings };
    /** A new game was started: modules holding derived state (lockup cars, mission progress) should reset. */
    newGameStarted: Record<string, never>;
  }
}

declare module '../core/hooks' {
  interface BhHooks {
    /** Current save data (JSON). */
    saveState(): Record<string, unknown>;
    /** Force a write; returns success. */
    saveNow(): boolean;
    /** Change settings as the Settings screen does (tests: e.g. Left-handed → ON). */
    updateSettings(patch: Partial<GameSettings>): void;
  }
}

const DEBOUNCE_SECONDS = 2.5;

export interface SaveSystemOptions {
  /** Inject storage (tests). Defaults to localStorage when available. */
  storage?: StorageLike | null;
}

export function createSaveSystem(opts: SaveSystemOptions = {}): System {
  let ctx: GameContext;
  let store: SaveStore;
  let data: SaveData = defaultSave();
  let status: LoadStatus = 'fresh';
  let dirtyFor = -1; // seconds since first unsaved change; -1 = clean
  const offs: (() => void)[] = [];
  let onHide: (() => void) | null = null;
  let suspended = false;

  const markDirty = (): void => {
    if (dirtyFor < 0) dirtyFor = 0;
  };
  const flush = (): boolean => {
    dirtyFor = -1;
    return store.save(data);
  };

  const service: SaveService = {
    get data() {
      return data;
    },
    get status() {
      return status;
    },
    get settings() {
      return data.settings;
    },
    get hasProgress() {
      return hasProgress(data);
    },
    updateSettings(patch) {
      Object.assign(data.settings, patch);
      markDirty();
      ctx.events.emit('settingsChanged', { settings: { ...data.settings } });
    },
    hasUnlock: (id) => data.unlocks.includes(id),
    addUnlock(id) {
      if (!data.unlocks.includes(id)) {
        data.unlocks.push(id);
        flush();
      }
    },
    isMissionComplete: (id) => data.completedMissions.includes(id),
    setArsenal(a) {
      if (suspended) return;
      data.arsenal = { owned: [...a.owned], current: a.current, loaded: { ...a.loaded }, reserve: { ...a.reserve } };
      markDirty();
    },
    recordJobRun(id, medal, seconds) {
      if (suspended || medal === 0) return null;
      const res = recordRun(data.jobRecords[id], medal, seconds);
      data.jobRecords[id] = res.record;
      flush();
      return res;
    },
    flush,
    markDirty,
    get suspended() {
      return suspended;
    },
    setSuspended(on) {
      suspended = on;
    },
    newGame() {
      const keep = data.settings;
      data = defaultSave();
      data.settings = keep;
      ctx.state.completedMissions = [];
      ctx.state.money = data.money;
      ctx.events.emit('newGameStarted', {});
      flush();
    },
  };

  return {
    id: 'save',
    order: 505,
    init(c) {
      ctx = c;
      store = new SaveStore(opts.storage === undefined ? browserStorage() : opts.storage);
      const res = store.load();
      data = res.data;
      status = res.status;
      if (status === 'corrupt') console.warn('[save] corrupt save found — moved aside, starting fresh');
      c.state.completedMissions = [...data.completedMissions];
      c.services.provide('save', service);

      offs.push(
        c.events.on('missionCompleted', (e) => {
          if (suspended) return;
          // A replay from the book is not progress: no new completion, no count (its pay still saves).
          if (e.replay) {
            queueMicrotask(flush);
            return;
          }
          if (!data.completedMissions.includes(e.id)) data.completedMissions.push(e.id);
          if (!c.state.completedMissions.includes(e.id)) c.state.completedMissions.push(e.id);
          data.stats.missionsCompleted++;
          // Economy (order 510) pays the reward in its own handler for this same event; write once it has.
          queueMicrotask(flush);
        }),
        c.events.on('moneyChanged', (e) => {
          if (suspended) return;
          data.money = e.amount;
          if (e.delta > 0 && e.reason !== 'newGame') data.stats.moneyEarned += e.delta;
          if (e.delta < 0) data.stats.moneySpent += -e.delta;
          markDirty();
        }),
        c.events.on('vehicleStolen', (e) => {
          if (suspended) return;
          if (e.byPlayer) data.stats.carsStolen++;
        }),
        // "Your record": the best getaway is the highest wanted level the police gave up on.
        c.events.on('heatCleared', (e) => {
          if (suspended || !e.escaped || e.previous <= data.stats.bestEscape) return;
          data.stats.bestEscape = Math.min(5, Math.round(e.previous));
          markDirty();
        }),
      );

      if (typeof document !== 'undefined') {
        onHide = (): void => {
          if (document.visibilityState === 'hidden') flush();
        };
        document.addEventListener('visibilitychange', onHide);
        window.addEventListener('pagehide', onHide);
      }

      c.hooks.register('saveState', () => JSON.parse(JSON.stringify(data)) as Record<string, unknown>);
      c.hooks.register('saveNow', () => flush());
      c.hooks.register('updateSettings', (patch) => service.updateSettings(patch));
      c.hooks.registerSnapshot('save', () => ({ status, available: store.available, completed: data.completedMissions, unlocks: data.unlocks, dirty: dirtyFor >= 0 }));
    },
    fixedUpdate(dt) {
      if (ctx.state.mode === 'playing' && !suspended) {
        data.stats.playTime += dt;
        // Miles driven (the profile page): metres at the wheel. Written with the play time; the debounce
        // already writes every few seconds while anything else changes, so no extra dirtying for this.
        const p = ctx.services.tryGet('player');
        if (p?.mode === 'driving' && p.vehicle) data.stats.distanceDriven += p.vehicle.speed * dt;
      }
      if (dirtyFor >= 0) {
        dirtyFor += dt;
        if (dirtyFor >= DEBOUNCE_SECONDS) flush();
      }
    },
    dispose() {
      if (dirtyFor >= 0) flush();
      for (const off of offs) off();
      offs.length = 0;
      if (onHide && typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onHide);
        window.removeEventListener('pagehide', onHide);
      }
    },
  };
}
